import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import PasswordInput from "../components/PasswordInput";

const MEGA_LOGO = "https://customer-assets.emergentagent.com/job_80904b6f-7286-4784-ad3d-292b160c5a80/artifacts/crjane52_MEGA%20CHAT%20LOGO.png";
const MESSENGER_LOGO = "/messenger-logo.png";

const RegisterPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const isMessenger = params.get("app") === "messenger";
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [recoveryEmail, setRecoveryEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [householdInviteCode, setHouseholdInviteCode] = useState("");
  const [referralCode, setReferralCode] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Username validation (matches backend rules)
    if (username.length < 8 || username.length > 16) {
      toast.error("Username must be 8-16 characters long");
      return;
    }
    if (!/^[a-zA-Z0-9_]+$/.test(username)) {
      toast.error("Username can only contain letters, numbers, and underscores (no spaces)");
      return;
    }
    if (username.startsWith("_") || username.endsWith("_")) {
      toast.error("Username cannot start or end with an underscore");
      return;
    }
    if (username.includes("__")) {
      toast.error("Username cannot contain two underscores in a row");
      return;
    }
    const bannedWords = ['mega', 'chat', 'admin', 'police', 'moderator', 'staff', 'support', 'official', 'system'];
    const usernameLower = username.toLowerCase();
    for (const word of bannedWords) {
      if (usernameLower.includes(word)) {
        toast.error(`Username cannot contain "${word}" or similar reserved words`);
        return;
      }
    }

    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/register`, { 
        username, 
        email, 
        recovery_email: recoveryEmail || null,
        password,
        household_invite_code: householdInviteCode.trim() ? householdInviteCode.trim().toUpperCase() : null,
        referral_code: referralCode.trim() ? referralCode.trim().toUpperCase() : null,
        source: isMessenger ? "messenger" : "chat",
      });
      toast.success(response.data.message);
      navigate(isMessenger ? "/login?app=messenger" : "/login");
    } catch (error) {
      // Safely extract error message (backend sometimes returns array of validation errors)
      let msg = "Registration failed";
      const detail = error.response?.data?.detail;
      if (typeof detail === "string") {
        msg = detail;
      } else if (Array.isArray(detail) && detail.length > 0) {
        msg = detail[0]?.msg || detail[0]?.message || msg;
      }
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a0a1f] via-[#1a0a3e] to-[#3a0a5c] flex items-start sm:items-center justify-center p-4 py-6 relative">
      {/* Decorative background logo watermark — fixed so it stays visible while page scrolls */}
      <img
        src={isMessenger ? MESSENGER_LOGO : MEGA_LOGO}
        alt=""
        aria-hidden="true"
        className="fixed inset-0 m-auto w-[85vmin] h-[85vmin] max-w-none object-contain opacity-[0.10] pointer-events-none select-none z-0"
      />
      <div className="retro-window w-full max-w-sm relative z-10 shadow-2xl shadow-black/50" data-testid="register-window">
        <div className="retro-title-bar">
          <span>{isMessenger ? "MEGA! Messenger - Create Account" : "MEGA! Chat - Create Account"}</span>
        </div>
        
        <div className="p-6 bg-[#C0C0C0]">
          {/* Logo */}
          <div className="text-center mb-4">
            <img 
              src={isMessenger ? MESSENGER_LOGO : MEGA_LOGO} 
              alt={isMessenger ? "MEGA Chat Messenger" : "MEGA Chat - World Wide Chat Rooms"}
              className="w-56 h-56 mx-auto object-contain drop-shadow-lg"
            />
            <p className="font-verdana text-sm text-[#7B0099] font-bold mt-2">{isMessenger ? "Join MEGA Chat Messenger" : "Create Your Account"}</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-3">
            <div>
              <label className="block font-verdana text-sm mb-1">Choose a Username:</label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="retro-input w-full"
                required
                minLength={8}
                maxLength={16}
                pattern="[a-zA-Z0-9_]+"
                data-testid="register-username-input"
              />
              <p className="font-arial text-xs text-[#808080] mt-1">8-16 characters, letters/numbers/underscores only. No spaces, no leading/trailing or double underscores.</p>
            </div>

            <div>
              <label className="block font-verdana text-sm mb-1">Email Address:</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="retro-input w-full"
                required
                data-testid="register-email-input"
              />
            </div>

            <div>
              <label className="block font-verdana text-sm mb-1">Recovery Email (Optional):</label>
              <input
                type="email"
                value={recoveryEmail}
                onChange={(e) => setRecoveryEmail(e.target.value)}
                className="retro-input w-full"
                data-testid="register-recovery-email-input"
              />
              <p className="font-arial text-xs text-[#808080] mt-1">Used for password recovery</p>
            </div>

            <div>
              <label className="block font-verdana text-sm mb-1">Password:</label>
              <PasswordInput
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="retro-input w-full"
                required
                minLength={6}
                testid="register-password-input"
              />
            </div>

            <div>
              <label className="block font-verdana text-sm mb-1">Confirm Password:</label>
              <PasswordInput
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="retro-input w-full"
                required
                testid="register-confirm-password-input"
              />
            </div>

            <div>
              <label className="block font-verdana text-sm mb-1 flex items-center gap-1">
                <span>Household Invite Code</span>
                <span className="text-[10px] text-gray-500">(optional)</span>
              </label>
              <input
                type="text"
                value={householdInviteCode}
                onChange={(e) => setHouseholdInviteCode(e.target.value.toUpperCase())}
                className="retro-input w-full uppercase tracking-widest font-mono"
                placeholder="ABC123XY"
                maxLength={10}
                data-testid="register-invite-code-input"
              />
              <p className="text-[10px] text-gray-600 mt-1">
                Got a family invite code? Enter it to bypass the 2-accounts-per-IP rule.
              </p>
            </div>

            <div>
              <label className="font-arial text-xs font-bold mb-1 flex items-center gap-1">
                <span>Referral Code</span>
                <span className="text-[10px] text-gray-500">(optional)</span>
              </label>
              <input
                type="text"
                value={referralCode}
                onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                className="retro-input w-full uppercase tracking-widest font-mono"
                placeholder="ABC123"
                maxLength={16}
                data-testid="register-referral-code-input"
              />
              <p className="text-[10px] text-gray-600 mt-1">
                A friend invite you? Enter their code — you'll both get free F-Chips! 🎁
              </p>
            </div>

            <div className="retro-inset p-2 text-xs font-arial">
              <p className="font-bold mb-1">Registration Info:</p>
              <ul className="list-disc ml-4 space-y-0.5">
                <li>We'll email you a verification link</li>
                <li>Click it to activate your account</li>
                <li>Then you can login and chat!</li>
              </ul>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="retro-button w-full py-2"
              data-testid="register-submit-btn"
            >
              {loading ? "Creating Account..." : "Create My Account"}
            </button>
          </form>

          <div className="mt-4 pt-3 border-t-2 border-[#808080]">
            <p className="font-arial text-xs text-center">
              Already have an account?{" "}
              <Link to={isMessenger ? "/login?app=messenger" : "/login"} className="text-[#0000EE] hover:underline" data-testid="login-link">
                Sign In
              </Link>
            </p>
          </div>

          {/* Credit */}
          <div className="mt-4 text-center">
            <p className="font-arial text-xs text-[#808080]">created by mr_fuker</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
